import { Expose, Type } from 'class-transformer';
import { type TFunction } from 'i18next';

import {
  AirLuggageJourney,
  type IAirLuggageJourney,
} from './journeys/AirLuggageJourney';
import {
  type IAirPayoutJourney,
  AirPayoutJourney,
} from './journeys/AirPayoutJourney';

export const MAX_LENGTH_JOURNEY_TITLE = 40;

export interface Airport {
  iata: string;

  city: string;
  cityTranslations: Record<string, string | undefined>;
  timeZone: string;
  name: string;
}

export interface Airline {
  uniqueIdentifier: string;

  code: string;
  iata: string;
  name: string;
  alternativeName: string | null;
  displayName: string;
}

export enum TripTier {
  COMPLETE = 'complete',
  ESSENTIAL = 'essential',
}

export interface EditableFlight {
  id: number;
  departureAirport: Airport | null;
  departureAirportCode: string | null;
  arrivalAirport: Airport | null;
  arrivalAirportCode: string | null;
  airline: Airline | null;
  airlineIdentifier: string | null;
  flightNumber: string | null;
  localDepartureDate: Date | null;
  metadata: {
    minDate: Date | string | null;
    maxDate: Date | string | null;
  };
}

export interface IFlightStatus {
  departureStatus: string;
  arrivalStatus: string;
  gmtDepartureDateTime: string;
  gmtArrivalDateTime: string;
  gmtEstimatedDepartureAt: string;
  gmtEstimatedArrivalAt: string;
  gmtActualDepartureAt: string;
  gmtActualArrivalAt: string;
}

export class FlightStatus implements IFlightStatus {
  departureStatus: string;
  arrivalStatus: string;
  gmtDepartureDateTime: string;
  gmtArrivalDateTime: string;
  gmtEstimatedDepartureAt: string;
  gmtEstimatedArrivalAt: string;
  gmtActualDepartureAt: string;
  gmtActualArrivalAt: string;
}

export interface IFlight {
  id: number;
  localDepartureDate: Date;
  flightIdentifier: string;
  flightNumber: string;
  gmtDepartureDateTime: string | null;
  gmtArrivalDateTime: string | null;
  active: boolean;
  arrivalAirport: Airport;
  departureAirport: Airport;
  airline: Airline;
  flightStatus: IFlightStatus | null;
}

export class Flight implements IFlight {
  id: number;

  @Type(() => Date)
  localDepartureDate: Date;

  flightIdentifier: string;
  flightNumber: string;

  gmtDepartureDateTime: string | null;
  gmtArrivalDateTime: string | null;

  active: boolean;

  arrivalAirport: Airport;
  departureAirport: Airport;

  airline: Airline;

  @Type(() => FlightStatus)
  flightStatus: FlightStatus | null;
}

export interface IFellowPassenger {
  id: number;
  firstName: string;
  lastName: string;
}

export class FellowPassenger implements IFellowPassenger {
  id: number;
  firstName: string;
  lastName: string;
}

export class Trip {
  id: number;

  uuid: string;
  tier: TripTier;

  @Type(() => Date)
  startOn: Date;
  @Type(() => Date)
  endOn: Date;

  brandName: string | null;
  coveredPassengersQuantity: number;

  upgradeable: boolean;
}

export interface IItinerary {
  id: number;
  remindToClaim: boolean;
  claimAllowed: boolean;
  active: boolean;
  fellowPassengers: IFellowPassenger[];
  flights: IFlight[];
}

export class Itinerary implements IItinerary {
  id: number;

  remindToClaim: boolean;

  claimAllowed: boolean;

  active: boolean;

  @Type(() => FellowPassenger)
  fellowPassengers: FellowPassenger[];

  @Type(() => Flight)
  flights: Flight[];
}

export enum AIR_BUNDLE_REJECTION_REASON {
  NO_CREDITS = 'no_credits',
  REJECTED = 'rejected',
}

export interface IAirRejections {
  airPayout: AIR_BUNDLE_REJECTION_REASON;
  airLuggage: AIR_BUNDLE_REJECTION_REASON;
}

export interface IJourney {
  id: number;
  name: string;
  active: boolean;
  loungeActive: boolean;
  itineraries: Itinerary[];
  trip: Trip | null;
  airPayoutJourneys: IAirPayoutJourney[];
  airLuggageJourneys: IAirLuggageJourney[];
  airRejections: IAirRejections | null;
  displayAirBundleActionNotification: boolean;
}

export class Journey implements IJourney {
  id: number;
  name: string;
  active: boolean;
  loungeActive: boolean;

  @Type(() => AirPayoutJourney)
  airPayoutJourneys: AirPayoutJourney[];

  @Expose()
  get hasAirPayout(): boolean {
    return this.airPayoutJourneys.length > 0;
  }

  @Type(() => AirLuggageJourney)
  airLuggageJourneys: AirLuggageJourney[];

  airRejections: IAirRejections;
  displayAirBundleActionNotification: boolean;

  @Expose()
  get airPayoutRejected(): boolean {
    return (
      this.airRejections.airPayout === AIR_BUNDLE_REJECTION_REASON.REJECTED
    );
  }

  @Expose()
  get airLuggageRejected(): boolean {
    return (
      this.airRejections.airLuggage === AIR_BUNDLE_REJECTION_REASON.REJECTED
    );
  }

  @Expose()
  get airPayoutNoCredits(): boolean {
    return (
      this.airRejections.airPayout === AIR_BUNDLE_REJECTION_REASON.NO_CREDITS
    );
  }

  @Expose()
  get airLuggageNoCredits(): boolean {
    return (
      this.airRejections.airLuggage === AIR_BUNDLE_REJECTION_REASON.NO_CREDITS
    );
  }

  @Expose()
  get hasAirLuggage(): boolean {
    return this.airLuggageJourneys.length > 0;
  }

  // From business perspective all air payout journeys are same so we base on a first one
  @Expose()
  get mainAirPayoutJourney(): AirPayoutJourney {
    return this.airPayoutJourneys[0];
  }

  // From business perspective all air luggage journeys are same so we base on a first one
  @Expose()
  get mainAirLuggageJourney(): AirLuggageJourney {
    return this.airLuggageJourneys[0];
  }

  @Type(() => Itinerary)
  itineraries: Itinerary[];

  @Type(() => Trip)
  trip: Trip | null;

  generateDisplayName(t: TFunction, locale: string): string {
    if (this.name) {
      return this.name;
    }
    const firstItinerary = this.itineraries[0];
    const lastFlight =
      firstItinerary.flights[firstItinerary.flights.length - 1];
    const destinationAirport = lastFlight.arrivalAirport;

    return t('journeys.name_default', {
      destination:
        destinationAirport?.cityTranslations[locale] ||
        destinationAirport?.city,
    });
  }
}
